import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import ToggleSwitch from 'react-switch';

import {
  CLIENT_MANUFACTURER_PRODUCT,
  CLIENT_MANUFACTURER_PRODUCT_UNIPRO_UPB_ELIGIBLE,
} from 'lib/networking/endpoints';
import { CUT_DRY_CLIENT_ID } from 'lib/constants';
import { grape } from 'lib/css/colors';
import { SwalToast } from 'lib/utils/toast';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';
import UserPermission from 'lib/enums/UserPermission';
import useUserPermissions from 'lib/hooks/useUserPermissions';

function UniproUpbToggle({ productId }) {
  const [isUniproUpbEligible, setIsUniproUpbEligible] = useState(false);
  const hasUserPermissions = useUserPermissions();

  const canManageUniproUpb = useMemo(
    () => hasUserPermissions([UserPermission.ADMIN_OPERATIONS]),
    [hasUserPermissions],
  );

  // Fetch the Unipro UPB eligible status for manufacturer products
  const { data: { data: clientMfrProduct } = {} } = useGet(
    CLIENT_MANUFACTURER_PRODUCT,
    { product_id: productId, client_id: CUT_DRY_CLIENT_ID },
    !productId,
  );

  // Update the Unipro UPB eligible status
  const { postData: updateUniproUpbEligible, loading: updatingUniproUpb } = usePost(
    CLIENT_MANUFACTURER_PRODUCT_UNIPRO_UPB_ELIGIBLE,
    () => SwalToast('success', 'Unipro UPB eligibility updated successfully'),
    () => SwalToast('error', 'Failed to update Unipro UPB eligibility'),
  );

  useEffect(() => {
    if (clientMfrProduct?.is_unipro_upb_eligible !== undefined) {
      setIsUniproUpbEligible(clientMfrProduct.is_unipro_upb_eligible);
    }
  }, [clientMfrProduct]);

  const handleUniproUpbToggle = () => {
    const newValue = !isUniproUpbEligible;
    setIsUniproUpbEligible(newValue);
    updateUniproUpbEligible({
      product_id: parseInt(productId, 10),
      client_id: CUT_DRY_CLIENT_ID,
      is_unipro_upb_eligible: newValue,
    });
  };

  if (!canManageUniproUpb) {
    return null;
  }

  return (
    <OverlayTrigger
      placement="left"
      overlay={
        <Tooltip id="unipro-upb-tooltip">
          Enable this to make the product visible in the Unipro UPB catalog view
        </Tooltip>
      }
    >
      <div className={css(styles.toggleContainer)}>
        <span className={css(styles.toggleLabel)}>Unipro UPB</span>
        <ToggleSwitch
          checked={isUniproUpbEligible}
          onChange={handleUniproUpbToggle}
          onColor={grape}
          offColor="#ccc"
          checkedIcon={false}
          uncheckedIcon={false}
          height={22}
          width={44}
          handleDiameter={18}
          disabled={updatingUniproUpb}
          activeBoxShadow={`0 0 2px 3px ${grape}40`}
        />
      </div>
    </OverlayTrigger>
  );
}

const styles = StyleSheet.create({
  toggleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 18px',
    backgroundColor: '#fff',
    borderRadius: '24px',
    border: `1px solid ${grape}30`,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.2s ease',
    ':hover': {
      boxShadow: `0 4px 12px ${grape}20`,
      borderColor: `${grape}50`,
    },
  },
  toggleLabel: {
    fontSize: '14px',
    fontWeight: '600',
    color: grape,
    userSelect: 'none',
  },
});

UniproUpbToggle.propTypes = {
  productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default UniproUpbToggle;
